/**
 * Workspace predictability profiles — Slice 4 Settings UI.
 * Overrides experimental / solid / default mixins used by Launch Calendar.
 */

import { useCallback, useEffect, useState } from 'react';
import { Loader2, RotateCcw, Save, SlidersHorizontal } from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import {
  defaultPredictabilityOverrides,
  getWorkspacePredictabilityProfiles,
  profilePreview,
  saveWorkspacePredictabilityProfiles,
  type ProfileSlot,
  type PredictabilityProfileOverride,
  type WorkspacePredictabilityProfiles,
} from '../../../lib/predictabilityProfilesService';

const SLOT_META: Record<
  ProfileSlot,
  { title: string; description: string }
> = {
  experimental: {
    title: 'Experimental',
    description: 'Spikes, research, prototypes — more slack, lower confidence, slides first.',
  },
  solid: {
    title: 'Solid / firm',
    description: 'GA, contractual, customer commitments — high confidence, slides last (often fixed).',
  },
  default: {
    title: 'Default',
    description: 'Everything else — modest slack and mid confidence.',
  },
};

function NumberField({
  label,
  value,
  min,
  max,
  step,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (n: number) => void;
}) {
  return (
    <label className="block text-sm">
      <span className="text-xs font-medium text-[var(--text-sec)]">{label}</span>
      <input
        type="number"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="mt-1 w-full border border-[var(--rule)] bg-[var(--bg-raised)] px-3 py-2 text-sm"
      />
    </label>
  );
}

function ProfileEditor({
  slot,
  value,
  onChange,
}: {
  slot: ProfileSlot;
  value: PredictabilityProfileOverride;
  onChange: (next: PredictabilityProfileOverride) => void;
}) {
  const preview = profilePreview(slot, value);
  const meta = SLOT_META[slot];
  return (
    <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-4 space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-[var(--text-pri)] m-0">{meta.title}</h3>
        <p className="text-xs text-[var(--text-ter)] mt-1 mb-0">{meta.description}</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <NumberField
          label="Uncertainty factor"
          value={value.uncertainty_factor}
          min={0.5}
          max={5}
          step={0.25}
          onChange={(n) => onChange({ ...value, uncertainty_factor: n })}
        />
        <NumberField
          label="Confidence ceiling (0–1)"
          value={value.confidence_ceiling}
          min={0.1}
          max={1}
          step={0.05}
          onChange={(n) => onChange({ ...value, confidence_ceiling: n })}
        />
        <NumberField
          label="Commitment firmness (1–10)"
          value={value.commitment_firmness}
          min={1}
          max={10}
          step={1}
          onChange={(n) => onChange({ ...value, commitment_firmness: n })}
        />
      </div>
      <p className="text-xs text-[var(--text-ter)]">
        Preview: ~{preview.slackWeeks}w slack · {(preview.calendarConfidence * 100).toFixed(0)}%
        confidence · suggests {preview.suggestedFlexibility}
      </p>
    </div>
  );
}

export default function PredictabilityProfilesSettings() {
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const [profiles, setProfiles] = useState<Record<ProfileSlot, PredictabilityProfileOverride>>(
    defaultPredictabilityOverrides(),
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    setError(null);
    try {
      const stored = await getWorkspacePredictabilityProfiles(workspaceId);
      setProfiles({ ...defaultPredictabilityOverrides(), ...stored } as Record<
        ProfileSlot,
        PredictabilityProfileOverride
      >);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load profiles');
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSave = async () => {
    if (!workspaceId) return;
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      const payload: WorkspacePredictabilityProfiles = {
        experimental: profiles.experimental,
        solid: profiles.solid,
        default: profiles.default,
      };
      await saveWorkspacePredictabilityProfiles(workspaceId, payload);
      setMessage('Saved — next Launch Calendar run uses these profiles.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
      <PageHeader
        title="Predictability profiles"
        subtitle="How work / launch / investment types behave on the Launch Calendar — slack, confidence ceiling, and conflict firmness."
        icon={<SlidersHorizontal className="h-5 w-5" />}
      />

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-[var(--text-ter)]" />
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {(['experimental', 'solid', 'default'] as ProfileSlot[]).map((slot) => (
              <ProfileEditor
                key={slot}
                slot={slot}
                value={profiles[slot]}
                onChange={(next) => setProfiles((prev) => ({ ...prev, [slot]: next }))}
              />
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving || !workspaceId}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save profiles
            </button>
            <button
              type="button"
              onClick={() => setProfiles(defaultPredictabilityOverrides())}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-[var(--rule)] text-[var(--text-sec)]"
            >
              <RotateCcw className="h-4 w-4" />
              Reset to defaults
            </button>
          </div>
          {message && <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)]">{message}</p>}
          {error && <p className="text-sm text-[var(--coral)]">{error}</p>}
        </>
      )}
    </div>
  );
}
